import { useHistory } from 'react-router-dom';
import { IonIcon } from '@ionic/react';
import { ribbon, school, checkmarkCircle, alertCircle, time, location } from 'ionicons/icons';
import type { ProviderSearchResult } from '@/lib/api/hooks';

const ACCENT_COLORS: [string, string][] = [
  ['#3b82f6', '#8b5cf6'], // blue → violet
  ['#06b6d4', '#3b82f6'], // cyan → blue
  ['#8b5cf6', '#ec4899'], // violet → pink
  ['#f59e0b', '#ef4444'], // amber → red
  ['#10b981', '#06b6d4'], // emerald → cyan
  ['#ec4899', '#f59e0b'], // pink → amber
];

const ACCREDITATION_STYLES: Record<string, { color: string; icon: string; bg: string }> = {
  Active: { color: '#34d399', icon: checkmarkCircle, bg: 'rgba(52, 211, 153, 0.12)' },
  Expired: { color: '#f87171', icon: alertCircle, bg: 'rgba(248, 113, 113, 0.12)' },
  Pending: { color: '#fbbf24', icon: time, bg: 'rgba(251, 191, 36, 0.12)' },
};

function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
}

function getAccent(idx: number): [string, string] {
  return ACCENT_COLORS[idx % ACCENT_COLORS.length];
}

export function ProviderCarouselCard({ provider, index }: { provider: ProviderSearchResult; index: number }) {
  const history = useHistory();
  const accreditation = provider.accreditationStatus ?? 'Active';
  const accStyle = ACCREDITATION_STYLES[accreditation] ?? ACCREDITATION_STYLES.Active;
  const [gradFrom, gradTo] = getAccent(index);
  const courseCount = provider.courses?.length ?? 0;

  return (
    <div
      className="bp-pc-card"
      onClick={() => history.push(`/providers/${provider._id}`)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && history.push(`/providers/${provider._id}`)}
    >
      {/* Gradient accent top bar */}
      <div className="bp-pc-accent-bar" style={{ background: `linear-gradient(90deg, ${gradFrom}, ${gradTo})` }} />

      <div className="bp-pc-card-inner">
        {/* Large avatar */}
        <div
          className="bp-pc-avatar"
          style={{
            background: `linear-gradient(135deg, ${gradFrom}, ${gradTo})`,
            boxShadow: `0 8px 32px ${gradFrom}44`,
          }}
        >
          <span className="bp-pc-avatar-text">{getInitials(provider.providerName)}</span>
        </div>

        {/* Provider name */}
        <h3 className="bp-pc-name">{provider.providerName}</h3>

        {/* Preferred badge */}
        {provider.tier === 'Preferred' && (
          <div className="bp-pc-preferred">
            <IonIcon icon={ribbon} />
            <span>Preferred Provider</span>
          </div>
        )}

        {/* Accreditation */}
        <div
          className="bp-pc-accreditation"
          style={{ color: accStyle.color, background: accStyle.bg }}
        >
          <IonIcon icon={accStyle.icon} />
          <span>SETA Accredited</span>
        </div>

        {/* Sectors */}
        <div className="bp-pc-sectors">
          {provider.industrySectors.slice(0, 3).map((s) => (
            <span key={s} className="bp-pc-sector-tag">{s}</span>
          ))}
        </div>

        {/* Info row */}
        <div className="bp-pc-info-row">
          {provider.physicalAddress?.province && (
            <div className="bp-pc-info-item">
              <IonIcon icon={location} />
              <span>{provider.physicalAddress.province}</span>
            </div>
          )}
          {courseCount > 0 && (
            <div className="bp-pc-info-item">
              <IonIcon icon={school} />
              <span>{courseCount} course{courseCount !== 1 ? 's' : ''}</span>
            </div>
          )}
          {provider.distanceKm !== undefined && (
            <div className="bp-pc-info-item bp-pc-distance">
              {Math.round(provider.distanceKm)} km
            </div>
          )}
        </div>

        {/* CTA */}
        <div className="bp-pc-cta">
          <span>View provider</span>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      </div>
    </div>
  );
}
